// Phase 2D — Persistent client-side cache for the Local Library page.
//
// Stale-while-revalidate strategy:
//   1. Mount → render `albums` from cache immediately, no spinner.
//   2. In parallel → GET /api/library/version.
//   3. If versions match → done.
//   4. If different → GET /api/library/diff?since=<cached.version>.
//        - full_refresh_required → fetch the full albums list, replace cache.
//        - else → merge added/modified into the cached list, drop removed[].
//   5. Cover-stats are cached alongside the albums and refreshed in the same
//      revalidation cycle so the redesigned header stays in sync.
//
// Why localStorage (and not IndexedDB):
//   ~1k albums × ~1.5 KB per row = ~1.5 MB JSON, well within localStorage
//   limits in modern browsers. Persistence is per-origin so the cache lives
//   across tab reloads, hot navigations, and full refreshes.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface CachedAlbum {
  id?: number | string;
  album_key?: string;
  artist_name?: string | null;
  title?: string | null;
  year?: number | null;
  genre?: string | null;
  format?: string | null;
  sample_rate?: number | null;
  bit_depth?: number | null;
  cover_art_url?: string | null;
  embedded_cover_hash?: string | null;
  folder_cover_hash?: string | null;
  user_override_source?: 'embedded' | 'folder' | 'online' | 'none' | null;
  enriched_at?: string | null;
  source_id?: string | null;
  machine_id?: string | null;
  track_count?: number;
  total_duration?: number;
  is_hires?: boolean;
  is_lossless?: boolean;
  is_compilation?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  __source?: string;
  isLocalLibrary?: boolean;
  // Carry through everything else the album-grid render path expects.
  [key: string]: any;
}

export interface CoverStats {
  total: number;
  embedded: number;
  folder: number;
  online: number;
  none: number;
  overridden: number;
  version?: string | null;
}

interface LibraryCacheState {
  /** Library version string (`<count>-<max_updated_at_iso>`). */
  version: string | null;
  /** Last full or merged albums list. Empty when cache is cold. */
  albums: CachedAlbum[];
  /** Cover-source counts shown in the redesigned header. */
  coverStats: CoverStats | null;
  /** Wall-clock ms when the cache was last hydrated from the server. */
  fetchedAt: number;

  // ── mutators ────────────────────────────────────────────────────────
  setSnapshot: (
    albums: CachedAlbum[],
    version: string | null,
    coverStats?: CoverStats | null,
  ) => void;
  setCoverStats: (stats: CoverStats | null) => void;
  applyDiff: (added: CachedAlbum[], modified: CachedAlbum[], removedIds: Array<string | number>) => void;
  setVersion: (version: string | null) => void;
  /** Optimistic per-album mutation (e.g. cover-source override).
   *  Pass a partial patch keyed by id OR album_key. Bumps version client-side
   *  with a temporary value so a subsequent /version poll still picks up the
   *  authoritative server version.
   */
  patchAlbum: (
    matcher: { id?: number | string; album_key?: string },
    patch: Partial<CachedAlbum>,
    optimisticVersion?: string | null,
  ) => void;
  clear: () => void;
}

const STORAGE_KEY = 'maestro:libraryCache';

export const useLibraryCacheStore = create<LibraryCacheState>()(
  persist(
    (set, get) => ({
      version: null,
      albums: [],
      coverStats: null,
      fetchedAt: 0,

      setSnapshot: (albums, version, coverStats) => {
        set({
          albums: Array.isArray(albums) ? albums : [],
          version: version || null,
          coverStats: coverStats !== undefined ? coverStats : get().coverStats,
          fetchedAt: Date.now(),
        });
      },

      setCoverStats: (stats) => {
        set({ coverStats: stats || null });
      },

      applyDiff: (added, modified, removedIds) => {
        const cur = get().albums;
        const removeSet = new Set((removedIds || []).map((x) => String(x)));
        // Build an index by id (preferred) AND album_key so the merge handles
        // both numeric ids and string keys depending on which the server returned.
        const byKey = new Map<string, CachedAlbum>();
        for (const a of cur) {
          const k = String(a?.id ?? a?.album_key ?? '');
          if (k && !removeSet.has(k)) byKey.set(k, a);
        }
        for (const a of modified || []) {
          const k = String(a?.id ?? a?.album_key ?? '');
          if (!k) continue;
          const prior = byKey.get(k);
          byKey.set(k, prior ? { ...prior, ...a } : a);
        }
        for (const a of added || []) {
          const k = String(a?.id ?? a?.album_key ?? '');
          if (!k || byKey.has(k)) continue;
          byKey.set(k, a);
        }
        // Re-sort by artist/title to match server ordering. The grid is
        // virtualized-by-page; stable order matters more than perfect locale.
        const next = Array.from(byKey.values()).sort((a, b) => {
          const ar = (a.artist_name || '').localeCompare(b.artist_name || '');
          if (ar !== 0) return ar;
          return (a.title || '').localeCompare(b.title || '');
        });
        set({ albums: next, fetchedAt: Date.now() });
      },

      setVersion: (version) => set({ version: version || null }),

      patchAlbum: (matcher, patch, optimisticVersion) => {
        const cur = get().albums;
        const next = cur.map((a) => {
          const idMatch = matcher.id != null && (a?.id != null) && String(a.id) === String(matcher.id);
          const keyMatch = matcher.album_key && a?.album_key === matcher.album_key;
          return idMatch || keyMatch ? { ...a, ...patch } : a;
        });
        set({
          albums: next,
          version: optimisticVersion !== undefined ? optimisticVersion : get().version,
        });
      },

      clear: () => set({ version: null, albums: [], coverStats: null, fetchedAt: 0 }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Persist only the raw cache fields. Function refs and computed values
      // would round-trip as `undefined` and pollute the persisted blob.
      partialize: (state) => ({
        version: state.version,
        albums: state.albums,
        coverStats: state.coverStats,
        fetchedAt: state.fetchedAt,
      }),
      version: 3,
      migrate: (persisted: unknown, fromVersion: number) => {
        // v2: remote tunnel albums now carry cover_art_url + machine_id — drop stale v1 cache.
        // v3: remote laptop must not reuse stale album keys / dead tunnel cover URLs.
        if (fromVersion < 3) {
          return {
            version: null,
            albums: [],
            coverStats: null,
            fetchedAt: 0,
          };
        }
        return persisted as Partial<LibraryCacheState>;
      },
    },
  ),
);
